package com.essntl.uikit.components.dropdown

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import core.validation.EsInputState

@Composable
fun <T> EsInputDropdownMenu(
    items: List<T>,
    selectedItem: T?,
    onItemSelected: (T) -> Unit,
    text: (T) -> String,
    suffixIcon: (T) -> String? = { null },
    loading: Boolean = false,
    starred: (T) -> Boolean = { false },
    label: String? = null,
    placeholder: String? = null,
    message: String? = null,
    enabled: Boolean = true,
    state: EsInputState = EsInputState.Default,
    modifier: Modifier = Modifier,
    onFocused: () -> Unit = {},
    isFocusedHandled: Boolean = false,
    defaultText: String? = null,
    topContent: @Composable ColumnScope.(FocusManager) -> Unit = {},
    emptyContent: @Composable (focusManager: FocusManager) -> Unit = { _ -> },
    bottomContent: @Composable ColumnScope.() -> Unit = {},
) {
    val focusManager = LocalFocusManager.current
    val interactionSource =
        remember {
            MutableInteractionSource()
        }

    val isFocused by interactionSource.collectIsFocusedAsState()

    LaunchedEffect(isFocused) {
        if (isFocused && !isFocusedHandled) onFocused()
    }

    BoxWithConstraints {
        EsInput(
            value = selectedItem?.let(text)
                .orEmpty()
                .ifEmpty { defaultText.orEmpty() },
            onValueChange = {},
            suffix = {
                EsMediumIcon(
                    res = EsIcons.ArrowDown,
                    tint = MaterialTheme.esColorScheme.iconPrimary,
                )
            },
            label = label,
            placeholder = placeholder,
            message = message,
            enabled = enabled,
            state = state,
            singleLine = true,
            cursorColor = Color.Transparent,
            interactionSource = interactionSource,
            modifier = modifier,
        )

        EsDropDownMenu(
            expanded = isFocused && enabled,
            onDismissRequest = {
                focusManager.clearFocus()
            },
            modifier =
                Modifier
                    .width(maxWidth),
            offset =
                DpOffset(
                    x = 0.dp,
                    y = (-20).dp,
                ),
        ) {
            topContent(focusManager)

            if (loading && items.isEmpty()) {
                EsSpinner(
                    color = MaterialTheme.esColorScheme.iconPrimary,
                    size = EsSpinnerSize.Large,
                    modifier =
                        Modifier
                            .align(Alignment.CenterHorizontally)
                            .padding(vertical = 1.spacing),
                )
            } else {
                if (items.isEmpty())
                    emptyContent(focusManager)
                else
                    items.forEach { item ->
                        EsDropdownMenuItem(
                            onClick = {
                                onItemSelected(item)
                                focusManager.clearFocus()
                            },
                            text = text(item),
                            suffixIcon = suffixIcon(item),
                            selected = item == selectedItem,
                            starred = starred(item),
                        )
                    }

                bottomContent()
            }
        }
    }
}

@Composable
fun <T> EsInputDropdownMenu(
    items: List<T>,
    selectedItems: List<T>,
    onItemsChanged: (List<T>) -> Unit,
    text: (T) -> String,
    loading: Boolean = false,
    label: String? = null,
    placeholder: String? = null,
    message: String? = null,
    enabled: Boolean = true,
    state: EsInputState = EsInputState.Default,
    modifier: Modifier = Modifier,
    topContent: @Composable ColumnScope.() -> Unit = {},
    bottomContent: @Composable ColumnScope.() -> Unit = {},
) {
    val focusManager = LocalFocusManager.current
    val interactionSource =
        remember {
            MutableInteractionSource()
        }
    val isFocused by interactionSource.collectIsFocusedAsState()

    BoxWithConstraints {
        EsInput(
            value =
                when (selectedItems.size) {
                    0 -> ""
                    1, 2, 3 -> selectedItems.joinToString(", ") { it?.let(text) ?: "" }
                    else ->
                        "${selectedItems.take(3).joinToString(",") { it?.let(text) ?: "" }}, " +
                            "+${selectedItems.size - 3}"
                },
            onValueChange = {},
            suffix = {
                EsMediumIcon(
                    res = EsIcons.ArrowDown,
                    tint = MaterialTheme.esColorScheme.iconPrimary,
                )
            },
            label = label,
            placeholder = placeholder,
            message = message,
            enabled = enabled,
            state = state,
            singleLine = true,
            cursorColor = Color.Transparent,
            interactionSource = interactionSource,
            modifier = modifier,
        )

        EsDropDownMenu(
            expanded = isFocused && enabled,
            onDismissRequest = {
                focusManager.clearFocus()
            },
            modifier =
                Modifier
                    .width(maxWidth),
            offset =
                DpOffset(
                    x = 0.dp,
                    y = (-20).dp,
                ),
        ) {
            topContent()

            if (loading) {
                EsSpinner(
                    color = MaterialTheme.esColorScheme.iconPrimary,
                    size = EsSpinnerSize.Large,
                    modifier =
                        Modifier
                            .align(Alignment.CenterHorizontally)
                            .padding(vertical = 1.spacing),
                )
            } else {
                items.forEach { item ->
                    val isSelected =
                        remember(item, selectedItems) {
                            item in selectedItems
                        }

                    EsDropdownMenuItem(
                        onClick = {
                            onItemsChanged(
                                if (isSelected) {
                                    selectedItems - item
                                } else {
                                    selectedItems + item
                                },
                            )
                        },
                        text = text(item),
                        selected = isSelected,
                    )
                }
                bottomContent()
            }
        }
    }
}
